// @flow
import type { Element } from 'react';

import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Page from '#components/support/layout/Page/index.js';
import Dashboard from '#components/pages/Models/Dashboard/index.jsx';
import Model from '#components/pages/Models/Model/index.js';
import ModelTypes from '#components/pages/Models/ModelTypes/index.js';
import ComponentsWindow from '#src/js/components/pages/Models/Components/index.js';
import UploadDecisionStep from '#components/pages/Models/CreateModel/UploadDecisionStep/index.js';
import UploadStep from '#components/pages/Models/CreateModel/UploadStep/index.js';
import ExtractionProgress from '#components/pages/Models/CreateModel/ExtractionProgress/index.js';

import {
  COMPONENTS,
  MODEL_ID,
  MODEL_TYPES,
  PROGRESS,
  UPLOAD,
  UPLOAD_DECISION,
} from '#constants/pathStrings.js';
import RequireType2ModelPermissions from '#components/support/routing/RequireType2ModelPermissions/index.js';
import RequireExtractionPermission from '#components/support/routing/RequireExtractionPermission/index.js';
import RequireCreateModelFlowStep from '#components/support/routing/RequireCreateModelFlowStep/index.js';

const Models = (): Element<typeof Page> => (
  <Routes>
    <Route path="/" element={<Dashboard />} />
    <Route path={`${MODEL_TYPES}`} element={<ModelTypes />} />
    {/* The creation flow's decision + upload + wait-in-progress steps (UI design §2.1/§2.2;
        HBR-4950 #316's decision page) -- registered ahead of the :modelId/* catch-all below so
        they take the already-created real model's own id, rather than being routed into the
        model form. Type selection creates the model and routes straight here (issue #127). The
        decision and upload steps are each also gated on the ephemeral createModelFlow step
        (RequireCreateModelFlowStep), so each is reachable through the UI only via its own
        in-flow control -- a refresh or deep link redirects into the model instead (decision 4). */}
    <Route
      path={`:${MODEL_ID}/${UPLOAD_DECISION}`}
      element={
        <RequireExtractionPermission>
          <RequireCreateModelFlowStep step="decide">
            <UploadDecisionStep />
          </RequireCreateModelFlowStep>
        </RequireExtractionPermission>
      }
    />
    <Route
      path={`:${MODEL_ID}/${UPLOAD}`}
      element={
        <RequireExtractionPermission>
          <RequireCreateModelFlowStep step="upload">
            <UploadStep />
          </RequireCreateModelFlowStep>
        </RequireExtractionPermission>
      }
    />
    <Route
      path={`:${MODEL_ID}/${PROGRESS}`}
      element={
        <RequireExtractionPermission>
          <ExtractionProgress />
        </RequireExtractionPermission>
      }
    />
    <Route path={`:${MODEL_ID}/*`} element={<Model />} />
    <Route
      path={`${COMPONENTS}/*`}
      element={
        <RequireType2ModelPermissions>
          <ComponentsWindow />
        </RequireType2ModelPermissions>
      }
    />
    <Route path="*" element={<Dashboard />} />
  </Routes>
);

export default Models;
